import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ExternalLink, PenLine, Send, Upload } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import LoadingState from 'components/shared/LoadingState';
import StageCard from './StageCard';
import StageVideo from './StageVideo';
import StageTranscript from './StageTranscript';
import StagePlayers from './StagePlayers';
import StageOutline from './StageOutline';
import StageWrite, { RewritePopoverBody } from './StageWrite';
import DocPreview from './DocPreview';
import {
  blogGenPreviewLink, blogGenSection, blogGenSetStatus, blogGenStart, blogGenState, blogPromptsList,
} from '../../../services/api';
import { ACTIVE, errMsg, useRunPoller } from './util';

const STATUS_VARIANT = { draft: 'secondary', pending_review: 'warning', published: 'success', archived: 'outline' };

export default function BlogGeneratorPage() {
  const { postId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const video = useMemo(() => {
    const id = params.get('video');
    return id ? { id, title: params.get('title') || id } : null;
  }, [params]);

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(!!postId);
  const [models, setModels] = useState({});
  const [open, setOpen] = useState({ 1: true });
  const [runIds, setRunIds] = useState({}); // stage → run id being polled
  const [conflict, setConflict] = useState(null);
  const [highlightTs, setHighlightTs] = useState(null);
  const [templateOverride, setTemplateOverride] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [sectionBusy, setSectionBusy] = useState(false);

  const post = state?.post;
  const isAdmin = !!state?.viewer?.is_admin;

  const load = useCallback(async (id) => {
    try {
      const st = await blogGenState(id);
      st.post._versions = st.versions;
      setState(st);
      return st;
    } catch (e) {
      toast.error(errMsg(e, 'Could not load the post'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    blogPromptsList().then((d) => setModels(d.models || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!postId) return;
    load(postId).then((st) => {
      if (!st) return;
      // resume: expand the right stage + pick up active runs
      const active = Object.values(st.runs || {}).filter((r) => ACTIVE.has(r.status));
      const ids = {};
      active.forEach((r) => { ids[r.stage] = r.id; });
      setRunIds(ids);
      const p = st.post;
      if (!p.extraction) setOpen({ 1: true });
      else if ((p.player_cards || []).some((c) => c.needs_review && c.role !== 'excluded')) setOpen({ 3: true });
      else if (!p.content?.content?.length) setOpen({ 4: true });
      else setOpen({ 5: true });
    });
  }, [postId, load]);

  const onRunDone = useCallback((stage) => async (run) => {
    if (run.status === 'succeeded') toast.success(`${stage === 'extract' ? 'Video read' : stage === 'write' ? 'Post written' : stage === 'verify' ? 'Draft checked' : 'Section rewritten'}`);
    else if (run.status === 'failed') toast.error(`${stage} failed: ${run.error}`);
    if (run.post_id) await load(run.post_id);
    if (stage === 'extract' && run.status === 'succeeded') setOpen({ 3: true });
    if (stage === 'write' && run.status === 'succeeded') setOpen({ 5: true });
  }, [load]);

  const extractRun = useRunPoller(runIds.extract, onRunDone('extract'));
  const writeRun = useRunPoller(runIds.write, onRunDone('write'));
  const verifyRun = useRunPoller(runIds.verify, onRunDone('verify'));
  const sectionRun = useRunPoller(runIds.section, onRunDone('section'));

  const runs = {
    extract: extractRun || state?.runs?.extract,
    write: writeRun || state?.runs?.write,
    verify: verifyRun || state?.runs?.verify,
    section: sectionRun || state?.runs?.section,
  };

  const start = async (opts, forceNew = false) => {
    try {
      const r = await blogGenStart({
        video_id: post?.source_video_id || video.id,
        video_title: post?.source_video_title || video.title,
        post_id: post?.id || null,
        force_new: forceNew,
        ...opts,
      });
      if (r.conflict) { setConflict({ posts: r.posts, opts }); return; }
      setConflict(null);
      if (!post || r.post_id !== post.id) {
        navigate(`/blog/generate/${r.post_id}`, { replace: true });
        setLoading(true);
        await load(r.post_id);
      }
      setRunIds((ids) => ({ ...ids, extract: r.run_id }));
      if (r.reused) toast.info('That extract is already running');
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onRunStarted = (stage, runId) => setRunIds((ids) => ({ ...ids, [stage]: runId }));
  const onState = (st) => { st.post._versions = st.versions; setState(st); };
  const setCards = (cards) => setState((s) => ({ ...s, post: { ...s.post, player_cards: cards } }));
  const setExtraction = (fn) => setState((s) => ({ ...s, post: { ...s.post, extraction: fn(s.post.extraction || {}) } }));
  const jump = (ts) => { setHighlightTs(ts); setOpen((o) => ({ ...o, 2: true })); };

  const setStatus = async (status) => {
    try {
      const p = await blogGenSetStatus(post.id, status);
      setState((s) => ({ ...s, post: { ...s.post, ...p } }));
      toast.success(status === 'published' ? 'Published' : status === 'pending_review' ? 'Sent for review' : `Status: ${status}`);
      setPublishOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const previewOnSite = async () => {
    try {
      const d = await blogGenPreviewLink(post.id);
      window.open(d.url, '_blank', 'noopener');
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const rewrite = async (heading, instruction) => {
    setSectionBusy(true);
    try {
      const r = await blogGenSection({ post_id: post.id, heading, instruction });
      onRunStarted('section', r.run_id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSectionBusy(false);
    }
  };

  if (loading) return <LoadingState message="Loading post…" />;
  if (!post && !video) {
    return <p className="p-6 text-muted-foreground">No video selected. <Button variant="link" onClick={() => navigate('/blog/generate')}>Pick a video</Button></p>;
  }

  const cards = post?.player_cards || [];
  const needs = cards.filter((c) => c.needs_review && c.role !== 'excluded').length;
  const hasDoc = post?.content?.content?.length > 0;
  const stageStatus = {
    1: runs.extract && ACTIVE.has(runs.extract.status) ? 'running' : runs.extract?.status === 'failed' ? 'failed' : post?.extraction ? 'done' : 'idle',
    2: state?.transcript ? 'done' : 'idle',
    3: !post?.extraction ? 'idle' : needs > 0 ? 'attention' : 'done',
    4: post?.extraction ? 'done' : 'idle',
    5: runs.write && ACTIVE.has(runs.write.status) ? 'running' : runs.write?.status === 'failed' ? 'failed' : hasDoc ? 'done' : 'idle',
  };
  const toggle = (n) => (o) => setOpen((prev) => ({ ...prev, [n]: o }));

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/blog')}><ArrowLeft /> Blog</Button>
        <h1 className="truncate text-token-lg font-semibold">{post?.title || video?.title || 'Blog Generator'}</h1>
        {post && <Badge variant={STATUS_VARIANT[post.status] || 'secondary'}>{post.status}</Badge>}
      </div>

      <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-3">
          <StageCard n={1} title="Video & context" status={stageStatus[1]} open={!!open[1]} onOpenChange={toggle(1)}
            summary={post?.extraction ? `${post.extraction.sections?.length || 0} sections · ${cards.length} players found` : 'Read the video and find the players'}>
            {conflict && (
              <div className="mb-3 rounded-md border border-warning/40 bg-warning/10 p-3 text-token-base">
                <p className="mb-2 font-medium">You already have a post from this video</p>
                {conflict.posts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-1">
                    <span>#{p.id} · {p.title} · <Badge variant={STATUS_VARIANT[p.status]}>{p.status}</Badge></span>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/blog/generate/${p.id}`)}>Continue</Button>
                  </div>
                ))}
                <Button size="sm" className="mt-2" onClick={() => start(conflict.opts, true)}>Start a new post anyway</Button>
              </div>
            )}
            <StageVideo post={post} video={video} blogTypes={state?.blog_types} models={models} run={runs.extract} onStart={(o) => start(o)} isAdmin={isAdmin || !post} />
          </StageCard>

          <StageCard n={2} title="Transcript" status={stageStatus[2]} open={!!open[2]} onOpenChange={toggle(2)} disabled={!state?.transcript}
            summary={state?.transcript ? `${Math.round(state.transcript.char_count / 1000)}k chars · click a timestamp to open YouTube there` : 'Available after the video is read'}>
            <StageTranscript transcript={state?.transcript} videoId={post?.source_video_id} highlightTs={highlightTs} cards={cards} />
          </StageCard>

          <StageCard n={3} title="Players" status={stageStatus[3]} open={!!open[3]} onOpenChange={toggle(3)} disabled={!post?.extraction}
            summary={post?.extraction ? `${needs} need a look · ${cards.filter((c) => c.role !== 'excluded').length} included` : 'Confirm who was actually discussed'}>
            {post?.extraction && (
              <StagePlayers postId={post.id} cards={cards} unresolved={post.extraction.unresolved || []} onCards={setCards} onExtraction={setExtraction} onJumpTs={jump} />
            )}
          </StageCard>

          <StageCard n={4} title="Outline & instructions" status={stageStatus[4]} open={!!open[4]} onOpenChange={toggle(4)} disabled={!post?.extraction}
            summary="Section notes, blog type, what to emphasise, and exactly what the writer gets">
            {post?.extraction && (
              <StageOutline post={post} blogTypes={state?.blog_types} onSaved={onState} templateOverride={templateOverride} setTemplateOverride={setTemplateOverride} isAdmin={isAdmin} onJumpTs={jump} />
            )}
          </StageCard>

          <StageCard n={5} title="Write" status={stageStatus[5]} open={!!open[5]} onOpenChange={toggle(5)} disabled={!post?.extraction}
            summary={hasDoc ? `Written · ${post.verify_report ? `${post.verify_report.counts?.contradicted ?? 0} contradicted claims` : 'not checked'}` : 'Generate the post, then rewrite any section'}>
            {post?.extraction && (
              <StageWrite post={post} models={models} runs={runs} onRunStarted={onRunStarted} templateOverride={templateOverride} onState={onState} verifyRun={runs.verify} sectionRun={runs.section} isAdmin={isAdmin} onJumpTs={jump} />
            )}
          </StageCard>
        </div>

        <div className="hidden rounded-token-large border border-borderGrey bg-white p-5 lg:block lg:max-h-[calc(100vh-180px)] lg:overflow-y-auto">
          {hasDoc ? (
            <>
              <h2 className="mb-1 text-token-xl font-semibold">{post.title}</h2>
              {post.excerpt && <p className="mb-4 text-token-base text-muted-foreground">{post.excerpt}</p>}
              <DocPreview doc={post.content} renderHeading={(text) => (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-6 text-[11px] font-normal" disabled={sectionBusy || (runs.section && ACTIVE.has(runs.section.status))}><PenLine /> Rewrite</Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px]" align="end">
                    <RewritePopoverBody busy={sectionBusy} onSubmit={(instr) => rewrite(text, instr)} />
                  </PopoverContent>
                </Popover>
              )} />
            </>
          ) : (
            <p className="text-token-base text-muted-foreground">The post preview appears here once it has been written.</p>
          )}
        </div>
      </div>

      {post && (
        <div className="sticky bottom-0 mt-4 flex flex-wrap items-center justify-between gap-2 rounded-token-large border border-borderGrey bg-white px-4 py-2">
          <div className="flex items-center gap-2 text-token-base">
            <Badge variant={STATUS_VARIANT[post.status] || 'secondary'}>{post.status}</Badge>
            <span className="text-muted-foreground">#{post.id} · GW{post.target_gw ?? '?'} · {post.season}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate(`/blog/edit/${post.id}`)} disabled={!hasDoc}><PenLine /> Open in editor</Button>
            <Button size="sm" variant="outline" onClick={previewOnSite} disabled={!hasDoc}><ExternalLink /> Preview on site</Button>
            {post.status !== 'pending_review' && post.status !== 'published' && (
              <Button size="sm" variant="outline" onClick={() => setStatus('pending_review')} disabled={!hasDoc}><Send /> Send to review</Button>
            )}
            {isAdmin && post.status !== 'published' && (
              <Button size="sm" onClick={() => setPublishOpen(true)} disabled={!hasDoc}><Upload /> Publish</Button>
            )}
          </div>
        </div>
      )}

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Publish this post?</DialogTitle></DialogHeader>
          <div className="space-y-1 text-token-base">
            <p><strong>{post?.title}</strong></p>
            <p>Gameweek {post?.target_gw ?? '?'} · Season {post?.season} · {state?.blog_type?.name || 'General post'} · {post?.is_premium ? 'Premium' : 'Free'}</p>
            {post?.verify_report?.counts?.contradicted > 0 && (
              <p className="text-destructive">The check found {post.verify_report.counts.contradicted} contradicted claim(s). Review them first?</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishOpen(false)}>Cancel</Button>
            <Button onClick={() => setStatus('published')}>Publish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
